import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

import Button from '../components/Button';
import AdminPage from '../components/AdminShell';
import './MarketingPages.css';

// Text filters, in toolbar order. `export` marks the ones the export endpoints also accept.
const FIELDS = [
  { key: 'name',        label: 'Name',      type: 'text',   placeholder: 'Player name' },
  { key: 'school_name', label: 'School',    type: 'text',   placeholder: 'School name', export: true },
  { key: 'state',       label: 'State',     type: 'text',   placeholder: 'e.g. IL', maxLength: 2, export: true },
  { key: 'grad_year',   label: 'Grad year', type: 'number', placeholder: 'e.g. 2026', export: true },
  { key: 'sport',       label: 'Sport',     type: 'text',   placeholder: 'e.g. Football', export: true },
  { key: 'position',    label: 'Position',  type: 'text',   placeholder: 'e.g. QB', export: true },
];
const FLAGS = [
  { key: 'profile_scraped', label: 'Profile loaded' },
  { key: 'has_social',      label: 'Has social' },
  { key: 'has_email',       label: 'Has email' },
];
const EMPTY_VALS = { name: '', school_name: '', state: '', grad_year: '', sport: '', position: '' };
const EMPTY_FLAGS = { profile_scraped: false, has_social: false, has_email: false };

const EXPORTS = [
  { value: 'csv', label: 'Full CSV', file: 'marketing_players.csv' },
  { value: 'twitter', label: 'Twitter handles', file: 'twitter_handles.csv' },
  { value: 'facebook', label: 'Facebook audiences', file: 'facebook_audiences.csv' },
];

export default function MarketingPlayersPage() {
  const { API_URL } = useAuth();
  const MARKETING_URL = `${API_URL.replace('/api', '')}/marketing`;

  const [players, setPlayers]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(1);
  const [pages, setPages]             = useState(1);
  const [loading, setLoading]         = useState(false);
  const [loadError, setLoadError]     = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const [vals, setVals]   = useState(EMPTY_VALS);
  const [flags, setFlags] = useState(EMPTY_FLAGS);

  // Export menu (merged in from the old Export page)
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [onlyEmail, setOnlyEmail] = useState(false);
  const [onlySocial, setOnlySocial] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');
  const exportRef = useRef(null);

  // Sort
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const away = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  const hasAnyFilter = Object.values(vals).some((v) => v.trim()) || Object.values(flags).some(Boolean);
  const clearAll = () => { setVals(EMPTY_VALS); setFlags(EMPTY_FLAGS); };

  const buildParams = useCallback((currentPage) => {
    const params = { page: currentPage, per_page: 50 };
    FIELDS.forEach((f) => {
      const v = vals[f.key].trim();
      if (!v) return;
      params[f.key] = f.key === 'grad_year' ? parseInt(v) : v;
    });
    FLAGS.forEach((f) => { if (flags[f.key]) params[f.key] = true; });
    return params;
  }, [vals, flags]);

  const fetchPlayers = useCallback(async (currentPage) => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${MARKETING_URL}/players`, { headers, params: buildParams(currentPage) });
      setPlayers(res.data.players);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) { console.error(err); setLoadError(true); }
    setLoading(false);
  }, [MARKETING_URL, buildParams]);

  const handleSearch = () => {
    setHasSearched(true); setPage(1); setSortCol(''); setSortDir('asc');
    fetchPlayers(1);
  };
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };
  const handlePageChange = (p) => { setPage(p); fetchPlayers(p); };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };
  const si = (col) => (sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  const getSorted = () => {
    if (!sortCol) return players;
    return [...players].sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case 'school':    av = (a.school_name || '').toLowerCase();  bv = (b.school_name || '').toLowerCase();  break;
        case 'state':     av = (a.school_state || '').toLowerCase(); bv = (b.school_state || '').toLowerCase(); break;
        case 'athlete':   av = (a.full_name || '').toLowerCase();    bv = (b.full_name || '').toLowerCase();    break;
        case 'grad_year': av = a.graduation_year || 0;               bv = b.graduation_year || 0;               break;
        case 'position':  av = (Array.isArray(a.position) ? a.position.join(', ') : a.position || '').toLowerCase();
                          bv = (Array.isArray(b.position) ? b.position.join(', ') : b.position || '').toLowerCase(); break;
        case 'profile':   av = a.profile_scraped ? 1 : 0;           bv = b.profile_scraped ? 1 : 0;            break;
        case 'views':     av = a.video_views || 0;                   bv = b.video_views || 0;                   break;
        default: return 0;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const viewDetail = async (playerId) => {
    try {
      const res = await axios.get(`${MARKETING_URL}/players/${playerId}`, { headers });
      setSelectedPlayer(res.data);
    } catch (err) { console.error(err); }
  };


  // Exports use the current text filters that the export endpoints understand (the name and profile-loaded
  // filters are search-only), plus the two flags chosen in the menu.
  const downloadExport = async () => {
    const fmt = EXPORTS.find((e) => e.value === exportFormat);
    const qs = new URLSearchParams();
    FIELDS.filter((f) => f.export).forEach((f) => { if (vals[f.key].trim()) qs.set(f.key, vals[f.key].trim()); });
    if (onlySocial || flags.has_social) qs.set('has_social', 'true');
    if (onlyEmail || flags.has_email) qs.set('has_email', 'true');
    setExporting(true);
    try {
      const url = `${MARKETING_URL}/export/${fmt.value}${qs.toString() ? `?${qs}` : ''}`;
      const res = await axios.get(url, { headers, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      link.download = fmt.file;
      link.click();
      URL.revokeObjectURL(link.href);
      setMessage(`Downloaded ${fmt.file}`);
      setExportOpen(false);
    } catch (err) {
      setMessage('Export failed. Check filters and try again.');
    }
    setExporting(false);
    setTimeout(() => setMessage(''), 3000);
  };

  const profileChip = (p) => (p.is_claimed || p.claimed
    ? <span className="adm-chip adm-chip--green">Claimed</span>
    : p.profile_scraped ? <span className="adm-chip">Scraped</span> : <span className="mkt-muted">—</span>);

  const exportAction = (
    <div className="mkt-export" ref={exportRef}>
      <button type="button" className="adm-btn adm-btn--dark" aria-haspopup="menu" aria-expanded={exportOpen} onClick={() => setExportOpen((v) => !v)}>
        Export CSV ▾
      </button>
      {exportOpen && (
        <div className="srch-menu mkt-export__menu" role="menu">
          <div className="srch-label">{hasSearched ? `EXPORT THESE ${total}` : 'EXPORT ALL PLAYERS'}</div>
          <select className="adm-select" aria-label="Export format" value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
            {EXPORTS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
          </select>
          <label className="srch-check"><input type="checkbox" checked={onlyEmail} onChange={(e) => setOnlyEmail(e.target.checked)} /><span>Has email only</span></label>
          <label className="srch-check"><input type="checkbox" checked={onlySocial} onChange={(e) => setOnlySocial(e.target.checked)} /><span>Has social only</span></label>
          <p className="mkt-export__note">Name and profile-loaded filters apply to the search only.</p>
          <button type="button" className="adm-btn adm-btn--primary" disabled={exporting} onClick={downloadExport}>{exporting ? 'Downloading…' : 'Download'}</button>
        </div>
      )}
    </div>
  );

  const th = (col, label, align) => (
    <th className={`adm-th--sortable ${align || ''}`} onClick={() => handleSort(col)}>{`${label}${si(col)}`}</th>
  );

  return (
    <AdminPage active="mkt-players" title="Players" actions={exportAction}
      subtitle={hasSearched ? `${total} players found` : 'Search athletes below'}>
      {message && <div className="ls-banner" role="status">{message}</div>}

      <div className="adm__card adm-toolbar mkt-filters">
        {FIELDS.map((f) => (
          <div className="adm-field" key={f.key}>
            <label className="adm-field__label" htmlFor={`mp_${f.key}`}>{f.label}</label>
            <input id={`mp_${f.key}`} className="adm-input" type={f.type} placeholder={f.placeholder} maxLength={f.maxLength}
              value={vals[f.key]} onChange={(e) => setVals((v) => ({ ...v, [f.key]: e.target.value }))} onKeyDown={handleKeyDown} />
          </div>
        ))}
        <div className="mkt-flags">
          {FLAGS.map((f) => (
            <label key={f.key} className="srch-check">
              <input type="checkbox" aria-label={f.label} checked={flags[f.key]} onChange={(e) => setFlags((v) => ({ ...v, [f.key]: e.target.checked }))} />
              <span>{f.label}</span>
            </label>
          ))}
        </div>
        <div className="mkt-filters__actions">
          {hasAnyFilter && <button type="button" className="srch-link" onClick={clearAll}>Clear all</button>}
          <button type="button" className="adm-btn adm-btn--primary" onClick={handleSearch}>Search</button>
        </div>
      </div>


      <div className="adm__card adm__card--flush">
        {!hasSearched ? (
          <p className="adm-empty">Use the filters above and choose Search to find athletes.</p>
        ) : loading ? (
          <p className="adm-loading">Loading…</p>
        ) : loadError ? (
          <p className="adm-error" role="alert">Could not load players.</p>
        ) : players.length === 0 ? (
          <p className="adm-empty">No players match your filters.</p>
        ) : (
          <>
            <div className="adm-tablewrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    {th('school', 'SCHOOL')}{th('state', 'STATE')}{th('athlete', 'ATHLETE')}{th('grad_year', 'GRAD')}
                    {th('position', 'POS')}{th('profile', 'PROFILE')}{th('views', 'VIDEO VIEWS', 'mkt-num')}
                  </tr>
                </thead>
                <tbody>
                  {getSorted().map((p) => (
                    <tr key={p.id} className="mkt-row" onClick={() => viewDetail(p.id)}>
                      <td>{p.school_name || '-'}</td>
                      <td className="mkt-muted">{p.school_state || '-'}</td>
                      <td className="mkt-school">{p.full_name}</td>
                      <td>{p.graduation_year || '-'}</td>
                      <td>{Array.isArray(p.position) ? p.position.join(', ') : p.position || '-'}</td>
                      <td>{profileChip(p)}</td>
                      <td className="mkt-num">{p.video_views != null ? p.video_views.toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="adm-pager">
                <button type="button" className="adm-btn" onClick={() => handlePageChange(Math.max(1, page - 1))} disabled={page === 1}>Prev</button>
                <span className="adm-pager__label">Page {page} of {pages}</span>
                <button type="button" className="adm-btn" onClick={() => handlePageChange(Math.min(pages, page + 1))} disabled={page === pages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>

      {selectedPlayer && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setSelectedPlayer(null)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: '620px', maxHeight: '85vh', overflow: 'auto' }}>
            <button className="modal-close" onClick={() => setSelectedPlayer(null)}>&times;</button>
            <div className="modal-title">{selectedPlayer.full_name}</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px', marginBottom: '16px' }}>
              <div><strong>School:</strong> {selectedPlayer.school?.name || '-'}</div>
              <div><strong>City/State:</strong> {selectedPlayer.school ? `${selectedPlayer.school.city || ''}, ${selectedPlayer.school.state || ''}` : '-'}</div>
              <div><strong>Grad Year:</strong> {selectedPlayer.graduation_year || '-'}</div>
              <div><strong>Position:</strong> {Array.isArray(selectedPlayer.position) ? selectedPlayer.position.join(', ') : selectedPlayer.position || '-'}</div>
              <div><strong>Height:</strong> {selectedPlayer.height || '-'}</div>
              <div><strong>Weight:</strong> {selectedPlayer.weight || '-'}</div>
              <div><strong>Jersey:</strong> {selectedPlayer.jersey_number || '-'}</div>
            </div>

            {selectedPlayer.social_links?.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                <strong style={{ fontSize: '14px' }}>Social Links:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                  {selectedPlayer.social_links.map((s, i) => (
                    <span key={i} style={{ padding: '3px 10px', background: '#f1f5f9', borderRadius: '12px', fontSize: '13px', fontWeight: 500 }}>
                      {s.platform}: {s.handle || s.profile_url || '-'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedPlayer.teams?.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                <strong style={{ fontSize: '14px' }}>Teams:</strong>
                {selectedPlayer.teams.map((t, i) => (
                  <div key={i} style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
                    {t.team_name} ({t.sport}){t.team_level ? ` · ${t.team_level}` : ''}{t.years_range ? ` | ${t.years_range}` : ''}
                  </div>
                ))}
              </div>
            )}

            {selectedPlayer.engagement && (
              <div style={{ marginBottom: '14px' }}>
                <strong style={{ fontSize: '14px' }}>Engagement:</strong>
                <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
                  Followers: {selectedPlayer.engagement.followers || 0} · Video Views: {(selectedPlayer.engagement.video_views || 0).toLocaleString()} · Highlights: {selectedPlayer.engagement.highlights || 0}
                </div>
              </div>
            )}

            {selectedPlayer.academics && (
              <div style={{ marginBottom: '14px' }}>
                <strong style={{ fontSize: '14px' }}>Academics:</strong>
                <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px' }}>
                  GPA: {selectedPlayer.academics.gpa || '-'} · SAT: {selectedPlayer.academics.sat_score || '-'} · ACT: {selectedPlayer.academics.act_score || '-'}
                </div>
              </div>
            )}

            {selectedPlayer.performance_metrics?.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                <strong style={{ fontSize: '14px' }}>Performance Metrics:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                  {selectedPlayer.performance_metrics.map((m, i) => (
                    <span key={i} style={{ padding: '3px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', fontSize: '12px' }}>
                      {m.name}: {m.value}{m.unit ? ` ${m.unit}` : ''}{m.verified ? ' ✓' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedPlayer.highlights?.length > 0 && (
              <div style={{ marginBottom: '14px' }}>
                <strong style={{ fontSize: '14px' }}>Highlight Reels ({selectedPlayer.highlights.length})</strong>
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '260px', overflowY: 'auto' }}>
                  {selectedPlayer.highlights.map((h, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '8px',
                      background: '#f8fafc', borderRadius: '6px',
                      border: h.is_featured ? '1px solid #3b82f6' : '1px solid #e5e7eb',
                    }}>
                      {h.thumbnail_url && (
                        <img src={h.thumbnail_url} alt="" onError={e => e.target.style.display = 'none'}
                          style={{ width: '72px', height: '44px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {h.is_featured && <span style={{ color: '#3b82f6', marginRight: '4px' }}>★</span>}
                          {h.title || 'Untitled'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          {h.total_views != null ? `${h.total_views.toLocaleString()} views` : ''}
                          {h.season ? ` · ${h.season}` : ''}
                          {h.highlight_type ? ` · ${h.highlight_type}` : ''}
                          {h.duration_ms ? ` · ${Math.round(h.duration_ms / 1000)}s` : ''}
                        </div>
                      </div>
                      {h.video_url && (
                        <Button
                          href={h.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="primary"
                          size="sm"
                          style={{ flexShrink: 0 }}
                        >▶ Watch</Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedPlayer.hudl_profile_url && (
              <a href={selectedPlayer.hudl_profile_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#3b82f6' }}>
                View Hudl Profile
              </a>
            )}
          </div>
        </div>
      )}
    </AdminPage>
  );
}
